import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../contexts/ThemeContext';
// Removed auto-save and keyboard navigation imports
import EnhancedFormInput from './EnhancedFormInput';
import FormSelect from './FormSelect';
import FormTextarea from './FormTextarea';
import LoadingSpinner from './LoadingSpinner';
import WizardErrorDisplay from './WizardErrorDisplay';
import { LoadingSkeleton } from './EnhancedLoadingState';
// import { ScaleTransition, FadeTransition } from './WizardTransition';
import { HelpTooltip } from './EnhancedTooltip';
import KnowledgeBaseConfiguration from './KnowledgeBaseConfiguration';
import KnowledgeBaseToggle from './KnowledgeBaseToggle';
// Removed unused module imports - now using KnowledgeBaseConfiguration component
import WizardNavigationConfirmDialog from './WizardNavigationConfirmDialog';
import { ApiClient } from '../utils/apiClient';
import { useWizardNavigation } from '../hooks/useWizardNavigation';
import { getWizardLayoutClasses, getTemplateGridClasses, getTouchFriendlyClasses, getResponsiveButtonClasses } from '../utils/responsiveUtils';
import {
  step1ValidationRules,
  validateStep1,
  validateParrotNameUniqueness,
  validateTemplateSelection
} from '../utils/wizardValidation';
import {
  callWizardApi,
  handleTemplateFallback,
  WizardError,
  WizardErrorCodes
} from '../utils/wizardErrorHandling';

/**
 * Step 1 Parrot Configuration Component
 * Handles basic parrot configuration including name, language, timing settings,
 * first message, and Quick Action template selection
 */
const Step1ParrotConfig = ({
  formData = {},
  onFormDataChange,
  onNext,
  onCancel,
  errors = {},
  isLoading = false,
  className='',
  isTransitioning = false,
  sessionManager = null
}) => {
  const componentId = useRef(Math.random().toString(36).substr(2, 9));
  // Reduced logging for performance
  // console.log(`Step1ParrotConfig[${componentId.current}]: Component render with formData:`, formData);
  const { getToken } = useAuth();
  const { theme } = useTheme();
  
  // Track if user has explicitly toggled the knowledge base checkbox
  const userToggledKnowledgeBaseRef = useRef(false);
  const apiClient = useRef(new ApiClient(getToken)).current;

  // Local state
  const [quickActionTemplates, setQuickActionTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [hoveredTemplate, setHoveredTemplate] = useState(null);
  const [localFormData, setLocalFormData] = useState({
    name: '',
    language: 'en', // Default to English
    max_call_length_seconds: '',
    turn_timeout_seconds: 7,
    max_silence_seconds: 20,
    first_message: '',
    selected_template_id: null,
    use_knowledge_base: false,
    ...formData
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [wizardError, setWizardError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Separate state for checkbox to ensure immediate UI updates
  const [checkboxEnabled, setCheckboxEnabled] = useState(false);

  // Knowledge Base state
  const [knowledgeBase, setKnowledgeBase] = useState({
    url: '',
    files: []
  });
  // Knowledge base state is now managed by KnowledgeBaseConfiguration component
  const isInitializedRef = useRef(false); // Track if component has been initialized
  // Knowledge base is now handled by KnowledgeBaseConfiguration component

  // Enhanced UX features
  const touchFriendlyClasses = getTouchFriendlyClasses();
  const layoutClasses = getWizardLayoutClasses();

  // Wizard navigation with unsaved changes protection
  const wizardNavigation = useWizardNavigation({
    sessionId: formData.session_id,
    parrotId: formData.parrot_id,
    currentStep: 1,
    onSaveChanges: async () => {
      // Save current form data before navigation
      // Requirements: 9.1, 9.2, 9.3 - Ensure wizard state preservation when navigating between steps
      const finalFormData = {
        ...localFormData,
        knowledge_base: localFormData.use_knowledge_base ? knowledgeBase : { url: '', files: [] },
        // Include sync status information for state preservation
        sync_status: knowledgeBase.files.reduce((acc, file) => {
          if (file.id) {
            acc[file.id] = {
              filename: file.name,
              status: file.sync_status || 'pending',
              error: file.sync_error || null
            };
          }
          return acc;
        }, {})
      };
      
      if (onFormDataChange) {
        onFormDataChange(finalFormData);
      }
      
      // Save to wizard state manager
      await wizardNavigation.saveCurrentStep(finalFormData, { completed: false });
    },
    autoSave: false
  });

  /**
   * Check if form has basic required fields for proceeding
   * Requirements: 9.6 - Add validation to prevent proceeding with failed syncs
   */
  const canProceed = () => {
    console.log('🔍 canProceed called');
    console.log('🔍 localFormData:', {
      name: localFormData.name,
      nameTrimmed: localFormData.name?.trim(),
      language: localFormData.language,
      firstMessage: localFormData.first_message,
      firstMessageTrimmed: localFormData.first_message?.trim(),
      use_knowledge_base: localFormData.use_knowledge_base
    });
    console.log('🔍 knowledgeBase.files:', knowledgeBase.files);
    
    const hasRequiredFields = localFormData.name.trim() &&
      localFormData.language &&
      localFormData.first_message.trim();
    
    console.log('🔍 hasRequiredFields:', hasRequiredFields);
    
    // If knowledge base is enabled, check for sync failures
    if (localFormData.use_knowledge_base && knowledgeBase.files.length > 0) {
      console.log('🔍 Checking knowledge base sync status...');
      const hasFailedSyncs = knowledgeBase.files.some(file => 
        file && file.sync_status && (file.sync_status === 'failed' || file.sync_status === 'error')
      );
      
      console.log('🔍 hasFailedSyncs:', hasFailedSyncs);
      console.log('🔍 File sync statuses:', knowledgeBase.files.map(f => ({
        id: f.id,
        name: f.name,
        sync_status: f.sync_status
      })));
      
      // TEMPORARY: Allow proceeding even with failed syncs for debugging
      // This should be reverted once the issue is identified
      console.log('⚠️ TEMPORARY: Bypassing sync validation for debugging');
      if (hasFailedSyncs) {
        console.log('❌ Found failed syncs but allowing to proceed for debugging');
        // return false; // Prevent proceeding with failed syncs
      }
    }
    
    const result = hasRequiredFields;
    console.log('🔍 canProceed result:', result);
    return result;
  };

  // Removed redundant useEffect - selectedKnowledgeBaseFiles is now only set in the main formData useEffect

  // Load Quick Action templates on mount
  useEffect(() => {
    fetchQuickActionTemplates();
  }, []);

  // Debounced parrot ID to prevent excessive API calls
  const debouncedParrotId = useMemo(() => {
    const timeoutId = setTimeout(() => formData.parrot_id, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.parrot_id]);

  // Knowledge base loading is now handled by KnowledgeBaseConfiguration component

  // Initialize local form data when component mounts or formData prop changes significantly
  useEffect(() => {
    // Step1ParrotConfig received formData

    // Only update if we receive new formData with actual content and it's different from current
    if (Object.keys(formData).length > 0 && (
      formData.parrot_id !== localFormData.parrot_id ||
      formData.use_knowledge_base !== localFormData.use_knowledge_base ||
      !isInitializedRef.current
    )) {
      // Updating local form data

      // Enhanced knowledge base configuration handling for editing mode
      // Requirements: 12.1, 12.2, 12.3 - Pre-populate existing knowledge base data
      let convertedKnowledgeBase = {
        url: '',
        files: [],
        textContent: []
      };

      if (formData.knowledge_base) {
        // Convert API format to component format with enhanced pre-population
        convertedKnowledgeBase = {
          url: formData.knowledge_base.url || '', // Convert null to empty string
          files: (formData.knowledge_base.files || []).map(file => ({
            ...file,
            // Ensure all required fields are present for pre-population
            id: file.id,
            name: file.name || file.original_filename || file.filename,
            size: file.size || file.file_size,
            type: file.type || 'file',
            mime_type: file.mime_type,
            sync_status: file.sync_status || 'synced',
            // Add display properties for UI
            selected: true, // Mark as selected for editing mode
            existing: true, // Mark as existing file
            created_at: file.created_at,
            updated_at: file.updated_at
          })),
          textContent: (formData.knowledge_base.text_documents || []).map(doc => ({
            ...doc,
            // Ensure all required fields are present
            id: doc.id,
            name: doc.name,
            content: doc.content_text || doc.content,
            sync_status: doc.sync_status || 'synced',
            // Add display properties for UI
            selected: true, // Mark as selected for editing mode
            existing: true, // Mark as existing document
            created_at: doc.created_at,
            updated_at: doc.updated_at
          }))
        };
        
        // Log pre-population for debugging
        console.log('Pre-populating knowledge base data for editing:', {
          url: convertedKnowledgeBase.url,
          filesCount: convertedKnowledgeBase.files.length,
          textContentCount: convertedKnowledgeBase.textContent.length,
          files: convertedKnowledgeBase.files.map(f => ({ id: f.id, name: f.name, selected: f.selected }))
        });
      }

      setKnowledgeBase(convertedKnowledgeBase);

      // Merge with defaults to ensure all required fields are present
      const updatedData = {
        name: '',
        language: 'en',
        max_call_length_seconds: '',
        turn_timeout_seconds: 7,
        max_silence_seconds: 20,
        first_message: '',
        selected_template_id: null,
        use_knowledge_base: false,
        ...formData // Override with provided data
      };

      // Enhanced auto-enable logic for editing mode
      // Requirements: 12.1, 12.2 - Auto-enable knowledge base if existing data is present
      const hasKnowledgeBaseContent = (
        (convertedKnowledgeBase.url && convertedKnowledgeBase.url.trim()) ||
        (convertedKnowledgeBase.files && convertedKnowledgeBase.files.length > 0) ||
        (convertedKnowledgeBase.textContent && convertedKnowledgeBase.textContent.length > 0)
      );

      // For editing mode, always respect existing knowledge base configuration
      if (formData.parrot_id && hasKnowledgeBaseContent) {
        // If we have existing KB content, enable KB unless explicitly disabled
        if (formData.use_knowledge_base !== false) {
          updatedData.use_knowledge_base = true;
          console.log('Auto-enabling knowledge base for editing mode due to existing content');
        }
      } else if (!userToggledKnowledgeBaseRef.current && hasKnowledgeBaseContent) {
        // For new parrots, auto-enable if there are knowledge base items
        updatedData.use_knowledge_base = true;
        console.log('Auto-enabling knowledge base for new parrot due to knowledge base items');
      }

      // use_knowledge_base value updated
      setLocalFormData(updatedData);
      isInitializedRef.current = true;
    }
  }, [formData.parrot_id, formData.use_knowledge_base, formData.knowledge_base]); // Watch for parrot_id, use_knowledge_base, and knowledge_base changes

  // Handle template selection when templates are loaded
  useEffect(() => {
    if (localFormData.selected_template_id && quickActionTemplates.length > 0) {
      // Looking for template ID
      const template = quickActionTemplates.find(t => t.id === localFormData.selected_template_id);
      // Found template
      if (template && (!selectedTemplate || selectedTemplate.id !== template.id)) {
        setSelectedTemplate(template);
        // Clear any template-related errors
        setWizardError(null);
      } else if (!template && quickActionTemplates.length > 0) {
        console.warn('Template not found, available templates:', quickActionTemplates.map(t => ({ id: t.id, name: t.name })));
      }
    }
  }, [localFormData.selected_template_id, quickActionTemplates]);

  // Remove the sync useEffect as it was causing issues - selectedKnowledgeBaseFiles should be managed independently
  // This was causing infinite loops because it was trying to sync state that was already being managed
  // by other useEffect hooks and state updates

  // Sync use_knowledge_base prop changes to local state
  useEffect(() => {
    // Don't sync if user has explicitly toggled the checkbox
    if (userToggledKnowledgeBaseRef.current) {
      console.log('🚫 Skipping prop sync - user has toggled checkbox');
      return;
    }
    
    if (formData.use_knowledge_base !== undefined && formData.use_knowledge_base !== localFormData.use_knowledge_base) {
      console.log('🔄 Syncing use_knowledge_base prop change:', {
        propValue: formData.use_knowledge_base,
        localValue: localFormData.use_knowledge_base,
        willUpdate: true,
        userToggled: userToggledKnowledgeBaseRef.current
      });
      setLocalFormData(prev => ({
        ...prev,
        use_knowledge_base: formData.use_knowledge_base
      }));
    }
  }, [formData.use_knowledge_base]); // Removed localFormData.use_knowledge_base from dependency array to prevent infinite loop

  // Sync checkbox state with form data
  useEffect(() => {
    setCheckboxEnabled(localFormData.use_knowledge_base);
  }, [localFormData.use_knowledge_base]);

  // Track when use_knowledge_base changes
  useEffect(() => {
    console.log('📊 use_knowledge_base state changed:', {
      value: localFormData.use_knowledge_base,
      checkboxEnabled: checkboxEnabled,
      userToggled: userToggledKnowledgeBaseRef.current,
      timestamp: new Date().toISOString()
    });
  }, [localFormData.use_knowledge_base]); // Removed checkboxEnabled from dependency array to prevent infinite loop

  // Notify parent component when form data changes - REMOVED to prevent infinite loops
  // Parent will be notified only on form submission

  // Remove real-time validation - only validate on form submission

  // Name uniqueness validation moved to form submission

  /**
   * Fetch active Quick Action templates from the API
   */
  const fetchQuickActionTemplates = async () => {
    try {
      setLoadingTemplates(true);
      setWizardError(null);

      const response = await apiClient.get('/api/quick-action-templates/active');
      // Templates API response received

      // Extract templates from response
      const templates = response.templates || response;
      // Extracted templates

      if (Array.isArray(templates) && templates.length > 0) {
        setQuickActionTemplates(templates);
        // Set templates
      } else {
        console.warn('No templates returned, using fallback');
        // Use fallback if no templates returned
        setQuickActionTemplates([{
          id: 1,
          name: 'General Template',
          description: 'A helpful AI assistant for general inquiries'
        }]);
      }
    } catch (error) {
      console.error('Failed to fetch Quick Action templates:', error);

      // Use fallback templates
      setQuickActionTemplates([{
        id: 1,
        name: 'General Template',
        description: 'A helpful AI assistant for general inquiries'
      }]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Knowledge base functions are now handled by KnowledgeBaseConfiguration component

  /**
   * Handle form field changes
   */
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;

    // Handle different input types
    if (type === 'checkbox') {
      processedValue = checked;
    } else if (type === 'number') {
      processedValue = value === '' ? '' : parseInt(value, 10);
    }

    const updatedData = {
      ...localFormData,
      [name]: processedValue
    };

    // If use_knowledge_base is being disabled, clear knowledge base completely
    let finalKnowledgeBase = knowledgeBase;
    if (name === 'use_knowledge_base' && !processedValue) {
      // Disabling use_knowledge_base, clearing all knowledge base data
      finalKnowledgeBase = { url: '', files: [] };
      setKnowledgeBase(finalKnowledgeBase);
      updatedData.knowledge_base = finalKnowledgeBase;
      
      // Knowledge base state is now handled internally by EnhancedKnowledgeBaseSection
    }

    setLocalFormData(updatedData);
    
    // Save to session manager if available
    if (sessionManager) {
      sessionManager.saveState(1, {
        ...updatedData,
        knowledge_base: finalKnowledgeBase
      }, { completed: false });
    }
    
    // Mark as having unsaved changes
    wizardNavigation.saveCurrentStep({
      ...updatedData,
      knowledge_base: finalKnowledgeBase
    }, { completed: false });
  };

  /**
   * Handle knowledge base configuration changes
   */
  const handleKnowledgeBaseChange = (newKnowledgeBase) => {
    // handleKnowledgeBaseChange called

    // Update knowledge base state
    setKnowledgeBase(newKnowledgeBase);

    // Auto-enable use_knowledge_base if files are uploaded and toggle is currently disabled
    // This ensures users can see their uploaded files immediately
    console.log('AUTO-ENABLE DEBUG:', {
      'localFormData.use_knowledge_base': localFormData.use_knowledge_base,
      'newKnowledgeBase.files exists': !!newKnowledgeBase.files,
      'newKnowledgeBase.files.length': newKnowledgeBase.files?.length || 0
    });

    const shouldEnableKnowledgeBase =
      !localFormData.use_knowledge_base &&
      newKnowledgeBase.files &&
      newKnowledgeBase.files.length > 0;

    // Should enable knowledge base check

    const updatedFormData = {
      ...localFormData,
      knowledge_base: newKnowledgeBase,
      ...(shouldEnableKnowledgeBase && { use_knowledge_base: true })
    };

    // Updated form data with knowledge base
    setLocalFormData(updatedFormData);

    // Notify parent component of the change
    if (onFormDataChange) {
      onFormDataChange(updatedFormData);
    }

    // Save KB configuration to session manager if available
    if (sessionManager && updatedFormData.use_knowledge_base) {
      const kbConfig = {
        files: newKnowledgeBase.files || [],
        urls: newKnowledgeBase.url ? [{
          id: `url_${Date.now()}`,
          url: newKnowledgeBase.url,
          status: 'validated',
          validation: { valid: true, accessible: true },
          added_at: new Date().toISOString()
        }] : [],
        textContent: [],
        metadata: {
          last_updated: new Date().toISOString(),
          total_files: newKnowledgeBase.files?.length || 0,
          total_urls: newKnowledgeBase.url ? 1 : 0,
          total_text_items: 0,
          validation_status: 'valid'
        }
      };
      
      sessionManager.saveKnowledgeBaseConfig(kbConfig);
      sessionManager.saveState(1, updatedFormData, { completed: false });
    } else if (sessionManager) {
      // Clear KB config if disabled
      sessionManager.clearKnowledgeBaseConfig();
      sessionManager.saveState(1, updatedFormData, { completed: false });
    }

    // Mark as having unsaved changes
    wizardNavigation.saveCurrentStep(updatedFormData, { completed: false });
  };

  /**
   * Validate URL format
   */
  const isValidUrl = (urlString) => {
    try {
      const url = new URL(urlString);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  };

  /**
   * Handle Quick Action template selection
   */
  const handleTemplateSelect = (template) => {
    const updatedData = {
      ...localFormData,
      selected_template_id: template.id,
      first_message: template.example_first_message || localFormData.first_message
    };

    setLocalFormData(updatedData);
    setSelectedTemplate(template);
  };

  /**
   * Clear template selection
   */
  const handleTemplateClear = () => {
    const updatedData = {
      ...localFormData,
      selected_template_id: null
    };

    setLocalFormData(updatedData);
    setSelectedTemplate(null);
  };

  /**
   * Validate form fields using enhanced validation (async for name uniqueness)
   */
  const validateForm = async () => {
    console.log('🔍 validateForm called with data:', localFormData);
    console.log('🔍 knowledgeBase state:', knowledgeBase);
    console.log('🔍 validationErrors before validation:', validationErrors);
    
    // Start with synchronous validation
    const validation = validateStep1(localFormData);
    console.log('🔍 validateStep1 result:', validation);
    console.log('🔍 validateStep1 validation errors:', validation.errors);
    let errors = { ...validation.errors };
    
    // Log each validation error for debugging
    if (Object.keys(errors).length > 0) {
      console.log('❌ Synchronous validation errors found:');
      Object.entries(errors).forEach(([field, error]) => {
        console.log(`   - ${field}: ${error}`);
      });
    } else {
      console.log('✅ No synchronous validation errors');
    }

    // Additional Knowledge Base validation
    if (localFormData.use_knowledge_base && knowledgeBase.url && knowledgeBase.url.trim()) {
      console.log('🔍 Validating knowledge base URL:', knowledgeBase.url);
      if (!isValidUrl(knowledgeBase.url)) {
        errors.knowledge_base_url = 'Invalid URL format';
        console.log('❌ URL validation failed');
      } else {
        console.log('✅ URL validation passed');
      }
    }

    console.log('🔍 Errors after URL validation:', errors);

    // Additional async validations
    if (validation.isValid && Object.keys(errors).length === 0) {
      console.log('🔍 Starting async validation...');
      try {
        // Check parrot name uniqueness
        // Validating name uniqueness
        console.log('🔍 About to call validateParrotNameUniqueness with:', {
          name: localFormData.name,
          parrot_id: formData.parrot_id,
          apiClient: !!apiClient
        });
        const isNameUnique = await validateParrotNameUniqueness(
          apiClient,
          localFormData.name,
          formData.parrot_id
        );
        
        console.log('🔍 Name uniqueness check result:', isNameUnique);

        // Fix: Handle undefined response from name uniqueness check
        const isNameUniqueResult = isNameUnique === true; // Only treat explicit true as success
        if (!isNameUniqueResult) {
          errors.name = 'A parrot with this name already exists';
          console.log('❌ Name uniqueness failed - isNameUnique:', isNameUnique, 'isNameUniqueResult:', isNameUniqueResult);
        } else {
          console.log('✅ Name uniqueness passed - isNameUnique:', isNameUnique, 'isNameUniqueResult:', isNameUniqueResult);
        }
        
        // Check if any errors were added during async validation
        if (Object.keys(errors).length > 0) {
          console.log('❌ Async validation errors found:');
          Object.entries(errors).forEach(([field, error]) => {
            console.log(`   - ${field}: ${error}`);
          });
        } else {
          console.log('✅ No async validation errors');
        }
      } catch (error) {
        console.warn('Async validation failed:', error);
        // Continue with form submission if async validation fails
      }
    }

    console.log('🔍 Final errors:', errors);
    
    // Log final validation summary
    const errorCount = Object.keys(errors).length;
    console.log('🔍 Validation result - isValid:', errorCount === 0);
    console.log('🔍 Total error count:', errorCount);
    
    if (errorCount > 0) {
      console.log('❌ FINAL VALIDATION ERRORS:');
      Object.entries(errors).forEach(([field, error]) => {
        console.log(`   - ${field}: ${error}`);
      });
    } else {
      console.log('✅ ALL VALIDATIONS PASSED');
    }
    
    setValidationErrors(errors);
    return errorCount === 0;
  };

  /**
   * Handle form submission with enhanced error handling
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 handleSubmit called');

    if (isSubmitting) {
      console.log('⚠️ Already submitting, returning');
      return;
    }

    try {
      setIsSubmitting(true);
      setWizardError(null);
      console.log('📋 Starting form validation...');

      const isValid = await validateForm();
      console.log('📋 Validation result:', isValid);
      console.log('📋 Current validation errors:', validationErrors);
      
      if (isValid) {
        console.log('✅ Form validation passed, proceeding with submission');
      } else {
        console.log('❌ Form validation failed:', validationErrors);
        // Display validation errors to user
        setWizardError({
          message: 'Please fix the validation errors before proceeding',
          details: Object.values(validationErrors).join(', '),
          code: 'VALIDATION_ERROR'
        });
        return; // Stop submission if validation fails
      }
      
      if (isValid) {
        // Include Knowledge Base data in form submission based on checkbox state
        // Requirements: 9.6 - Add validation to prevent proceeding with failed syncs
        console.log('Step1ParrotConfig: localFormData before creating finalFormData:', localFormData);
        console.log('Step1ParrotConfig: knowledgeBase before creating finalFormData:', knowledgeBase);
        
        const finalFormData = {
          ...localFormData,
          // Include knowledge base configuration only if enabled, otherwise clear it
          knowledge_base: localFormData.use_knowledge_base ? knowledgeBase : { url: '', files: [] },
          // Include legacy-compatible selection so backend can persist existing files
          selected_rag_files: localFormData.use_knowledge_base
            ? (knowledgeBase.files || [])
                .filter(file => file && file.id && !file.temp_id)
                .map(file => ({
                  id: file.id,
                  name: file.name,
                  size: file.size,
                  mime_type: file.mime_type || file.type
                }))
            : [],
          // Include sync status for state preservation
          sync_status: knowledgeBase.files.reduce((acc, file) => {
            if (file.id) {
              acc[file.id] = {
                filename: file.name,
                status: file.sync_status || 'pending',
                error: file.sync_error || null
              };
            }
            return acc;
          }, {})
        };

        // Validate sync status before proceeding
        if (localFormData.use_knowledge_base && knowledgeBase.files.length > 0) {
          console.log('🔍 Checking knowledge base sync status:', knowledgeBase.files);
          const failedSyncs = knowledgeBase.files.filter(file => 
            file.sync_status === 'failed' || file.sync_status === 'error'
          );
          
          console.log('🔍 Failed syncs found:', failedSyncs);
          
          // TEMPORARY: Allow proceeding even with failed syncs for debugging
          if (failedSyncs.length > 0) {
            const errorMsg = `Cannot proceed with ${failedSyncs.length} failed sync${failedSyncs.length !== 1 ? 's' : ''}. Please retry or remove the failed files.`;
            console.log('❌ Sync validation failed:', errorMsg);
            console.log('⚠️ TEMPORARY: Bypassing sync validation for debugging - would normally throw error here');
            // throw new Error(errorMsg);
          } else {
            console.log('✅ All syncs are valid');
          }
        }

        // Final form data being submitted
        console.log('Step1ParrotConfig: Final form data being submitted:', finalFormData);

        // Save to session manager if available
        if (sessionManager) {
          sessionManager.saveState(1, finalFormData, { completed: true });
          
          // Save KB configuration
          if (finalFormData.use_knowledge_base && finalFormData.knowledge_base) {
            const kbConfig = {
              files: finalFormData.knowledge_base.files || [],
              urls: finalFormData.knowledge_base.url ? [{
                id: `url_${Date.now()}`,
                url: finalFormData.knowledge_base.url,
                status: 'validated',
                validation: { valid: true, accessible: true },
                added_at: new Date().toISOString()
              }] : [],
              textContent: [],
              metadata: {
                last_updated: new Date().toISOString(),
                total_files: finalFormData.knowledge_base.files?.length || 0,
                total_urls: finalFormData.knowledge_base.url ? 1 : 0,
                total_text_items: 0,
                validation_status: 'valid'
              }
            };
            
            sessionManager.saveKnowledgeBaseConfig(kbConfig);
          }
        }

        // Save to wizard state manager as completed
        await wizardNavigation.saveCurrentStep(finalFormData, { completed: true });

        // Notify parent component with final form data
        if (onFormDataChange) {
          onFormDataChange(finalFormData);
        }

        // Proceed to next step
        if (onNext) {
          await onNext(finalFormData);
        }
      }
    } catch (error) {
      console.error('❌ Form submission error:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name,
        code: error.code
      });
      
      // Enhanced error display
      setWizardError({
        message: error.message || 'An error occurred while submitting the form',
        details: error.details || error.stack || 'No additional details available',
        code: error.code || 'SUBMISSION_ERROR',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Retry failed operations
   */
  const handleRetry = () => {
    setWizardError(null);
    if (loadingTemplates) {
      fetchQuickActionTemplates();
    }
  };

  /**
   * Dismiss error messages
   */
  const handleDismissError = () => {
    setWizardError(null);
  };

  // Language options
  const languageOptions = [
    { value: 'en', label: 'English' }
    // Additional languages can be added here when supported
  ];

  return (
    <div className={`step1-parrot-config ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${theme === 'dark' ? 'bg-teal-900' : 'bg-teal-100'
          }`}>
          <svg className={`w-10 h-10 ${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h2 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
          Configure Your Parrot
        </h2>
        <p className={`text-lg ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
          }`}>
          Let's start with the basic settings for your AI voice assistant
        </p>
      </div>

      {/* Error Display */}
      {wizardError && (
        <WizardErrorDisplay
          error={wizardError}
          onRetry={handleRetry}
          onDismiss={handleDismissError}
          context="Step 1: Basic Configuration"
          className="mb-6"
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information Section */}
        <div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
          }`}>
          <h3 className={`text-xl font-semibold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
            Basic Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <EnhancedFormInput
              label="Parrot Name"
              name="name"
              type="text"
              placeholder="e.g., Customer Support Assistant"
              required={true}
              value={localFormData.name}
              onChange={handleInputChange}
              error={errors.name || validationErrors.name}
              hasError={!!(errors.name || validationErrors.name)}
              helpText="Choose a friendly name that represents your business"
              disabled={isLoading || isSubmitting}
              validationRules={step1ValidationRules.name}
              showValidationIcon={true}
            />

            <FormSelect
              label="Language"
              name="language"
              options={languageOptions}
              placeholder="Select language"
              required={true}
              value={localFormData.language}
              onChange={handleInputChange}
              error={validationErrors.language || errors.language}
              hasError={!!(validationErrors.language || errors.language)}
              helpText="Currently only English is supported"
              disabled={isLoading || isSubmitting}
            />
          </div>
        </div>

        {/* Timing Settings Section */}
        <div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
          }`}>
          <h3 className={`text-xl font-semibold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
            Call Timing Settings
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <EnhancedFormInput
              label="Maximum Call Length"
              name="max_call_length_seconds"
              type="number"
              placeholder="Leave blank for unlimited"
              value={localFormData.max_call_length_seconds}
              onChange={handleInputChange}
              error={validationErrors.max_call_length_seconds || errors.max_call_length_seconds}
              hasError={!!(validationErrors.max_call_length_seconds || errors.max_call_length_seconds)}
              helpText="Maximum duration in seconds (30-3600, blank for unlimited)"
              disabled={isLoading || isSubmitting}
              validationRules={step1ValidationRules.max_call_length_seconds}
              min="30"
              max="3600"
            />

            <EnhancedFormInput
              label="Turn Timeout"
              name="turn_timeout_seconds"
              type="number"
              placeholder="7"
              required={true}
              value={localFormData.turn_timeout_seconds}
              onChange={handleInputChange}
              error={validationErrors.turn_timeout_seconds || errors.turn_timeout_seconds}
              hasError={!!(validationErrors.turn_timeout_seconds || errors.turn_timeout_seconds)}
              helpText="Seconds to wait for caller response (1-30)"
              disabled={isLoading || isSubmitting}
              validationRules={step1ValidationRules.turn_timeout_seconds}
              min="1"
              max="30"
            />

            <EnhancedFormInput
              label="Maximum Silence Time"
              name="max_silence_seconds"
              type="number"
              placeholder="20"
              value={localFormData.max_silence_seconds}
              onChange={handleInputChange}
              error={validationErrors.max_silence_seconds || errors.max_silence_seconds}
              hasError={!!(validationErrors.max_silence_seconds || errors.max_silence_seconds)}
              helpText="Seconds before ending call (5-120, blank for unlimited)"
              disabled={isLoading || isSubmitting}
              validationRules={step1ValidationRules.max_silence_seconds}
              min="5"
              max="120"
            />
          </div>
        </div>

        {/* Quick Action Templates Section */}
        <div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
          }`}>
          <h3 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
            Quick Action Templates
          </h3>
          <p className={`text-sm mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
            Choose a pre-configured template to get started quickly, or skip to create a custom configuration
          </p>

          {loadingTemplates ? (
            <LoadingSkeleton type="card" count={3} />
          ) : (
            <>
              {/* Selected Template Display */}
              {selectedTemplate && (
                <div className={`mb-6 p-4 rounded-lg border-l-4 border-teal-500 ${theme === 'dark' ? 'bg-teal-900/20' : 'bg-teal-50'
                  }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className={`font-medium ${theme === 'dark' ? 'text-teal-300' : 'text-teal-800'
                        }`}>
                        Selected: {selectedTemplate.name}
                      </h4>
                      <p className={`text-sm ${theme === 'dark' ? 'text-teal-400' : 'text-teal-600'
                        }`}>
                        {selectedTemplate.description}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleTemplateClear}
                      className={`text-sm px-3 py-1 rounded ${theme === 'dark'
                        ? 'text-teal-300 hover:bg-teal-800'
                        : 'text-teal-700 hover:bg-teal-100'
                        }`}
                      disabled={isLoading}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {/* Template Grid */}
              <div className={getTemplateGridClasses(quickActionTemplates.length)}>
                {quickActionTemplates.map((template) => (
                  <div key={template.id}>
                    <div
                      className={`relative p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${touchFriendlyClasses.clickable} ${selectedTemplate?.id === template.id
                        ? theme === 'dark'
                          ? 'border-teal-500 bg-teal-900/20 ring-2 ring-teal-500/20'
                          : 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/20'
                        : theme === 'dark'
                          ? 'border-gray-600 bg-gray-700 hover:border-gray-500 hover:shadow-lg'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:shadow-lg'
                        } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} ${hoveredTemplate?.id === template.id ? 'transform scale-105' : ''
                        }`}
                      onClick={() => !isLoading && handleTemplateSelect(template)}
                      onMouseEnter={() => setHoveredTemplate(template)}
                      onMouseLeave={() => setHoveredTemplate(null)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if ((e.key === 'Enter' || e.key === ' ') && !isLoading) {
                          e.preventDefault();
                          handleTemplateSelect(template);
                        }
                      }}
                      aria-pressed={selectedTemplate?.id === template.id}
                      aria-describedby={`template-${template.id}-description`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <h4 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                            {template.name}
                          </h4>
                          {template.system_prompt && (
                            <HelpTooltip
                              content={
                                <div className="max-w-xs">
                                  <p className="font-medium mb-2">Preview:</p>
                                  <p className="text-xs">
                                    {template.system_prompt.substring(0, 200)}
                                    {template.system_prompt.length > 200 ? '...' : ''}
                                  </p>
                                </div>
                              }
                              position="top"
                            />
                          )}
                        </div>
                        {selectedTemplate?.id === template.id && (
                          <svg className="w-5 h-5 text-teal-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <p
                        id={`template-${template.id}-description`}
                        className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}
                      >
                        {template.description}
                      </p>

                      {/* Hover Preview */}
                      {hoveredTemplate?.id === template.id && template.system_prompt && (
                        <div className={`absolute z-10 top-full left-0 right-0 mt-2 p-3 rounded-lg shadow-lg border ${theme === 'dark'
                          ? 'bg-gray-900 border-gray-600 text-gray-300'
                          : 'bg-white border-gray-200 text-gray-700'
                          }`}>
                          <p className="text-xs font-medium mb-1">Preview:</p>
                          <p className="text-xs">
                            {template.system_prompt?.substring(0, 150)}
                            {template.system_prompt?.length > 150 ? '...' : ''}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {quickActionTemplates.length === 0 && (
                <div className={`text-center py-8 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                  <p>No templates available at the moment.</p>
                  <p className="text-sm mt-1">You can still create a custom parrot configuration.</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* First Message Section */}
        <div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
          }`}>
          <h3 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
            First Message
          </h3>

          <FormTextarea
            label="What should your parrot say when answering calls?"
            name="first_message"
            placeholder="e.g., Hello! Thank you for calling [Your Business]. How can I help you today?"
            required={true}
            value={localFormData.first_message}
            onChange={handleInputChange}
            error={errors.first_message || validationErrors.first_message}
            hasError={!!(errors.first_message || validationErrors.first_message)}
            helpText="This message will be spoken word-for-word at the beginning of each call"
            disabled={isLoading || isSubmitting}
            rows={3}
          />
        </div>

        {/* Enhanced Knowledge Base Configuration Section */}
        <div className={`rounded-lg p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white border border-gray-200'
          }`}>
          <h3 className={`text-xl font-semibold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
            Knowledge Base Configuration
          </h3>
          
          {/* Knowledge Base Toggle Component */}
          <KnowledgeBaseToggle
            enabled={checkboxEnabled}
            onToggle={(enabled) => {
              console.log('🔄 Knowledge Base Toggle clicked:', {
                enabled,
                currentState: localFormData.use_knowledge_base,
                checkboxState: checkboxEnabled,
                userToggled: userToggledKnowledgeBaseRef.current
              });

              // Immediately update checkbox state for instant UI feedback
              setCheckboxEnabled(enabled);

              // Mark that user has explicitly toggled the checkbox
              userToggledKnowledgeBaseRef.current = true;

              // Update form data with toggle state
              const updatedFormData = {
                ...localFormData,
                use_knowledge_base: enabled
              };

              console.log('📝 Updating form data:', {
                from: localFormData.use_knowledge_base,
                to: enabled,
                updatedFormData: updatedFormData
              });

              // Clear knowledge base data when disabling
              if (!enabled) {
                console.log('🧹 Clearing knowledge base data (disabled)');
                const clearedKnowledgeBase = { url: '', files: [], textContent: [] };
                setKnowledgeBase(clearedKnowledgeBase);
                updatedFormData.knowledge_base = clearedKnowledgeBase;
              }
              
              setLocalFormData(prevState => {
                console.log('🔄 Functional state update:', {
                  prevState: prevState.use_knowledge_base,
                  newState: updatedFormData.use_knowledge_base,
                  fullUpdate: updatedFormData
                });
                return updatedFormData;
              });
              
              // Debug: Check state after update
              console.log('🔍 State update completed:', {
                newState: updatedFormData.use_knowledge_base,
                hasKnowledgeBase: updatedFormData.knowledge_base,
                timestamp: new Date().toISOString()
              });
              
              // Notify parent component of the change
              if (onFormDataChange) {
                console.log('📤 Notifying parent component of change:', updatedFormData);
                onFormDataChange(updatedFormData);
              } else {
                console.warn('⚠️ No onFormDataChange callback provided');
              }
              
              // Save to session manager if available
              if (sessionManager) {
                sessionManager.setKnowledgeBaseEnabled(enabled);
                
                if (enabled) {
                  // Initialize KB config when enabling
                  const kbConfig = {
                    enabled: true,
                    files: knowledgeBase.files || [],
                    urls: knowledgeBase.url ? [{
                      id: `url_${Date.now()}`,
                      url: knowledgeBase.url,
                      status: 'validated',
                      validation: { valid: true, accessible: true },
                      added_at: new Date().toISOString()
                    }] : [],
                    textContent: [],
                    metadata: {
                      enabled: true,
                      last_updated: new Date().toISOString(),
                      total_files: knowledgeBase.files?.length || 0,
                      total_urls: knowledgeBase.url ? 1 : 0,
                      total_text_items: 0,
                      validation_status: 'valid'
                    }
                  };
                  sessionManager.saveKnowledgeBaseConfig(kbConfig);
                } else {
                  // Clear KB config when disabling
                  sessionManager.clearKnowledgeBaseConfig();
                }
                sessionManager.saveState(1, updatedFormData, { completed: false });
              }
              
              wizardNavigation.saveCurrentStep(updatedFormData, { completed: false });
            }}
            onClear={() => {
              // Clear URL and file selections when knowledge base is disabled
              const clearedKnowledgeBase = { url: '', files: [], textContent: [] };
              setKnowledgeBase(clearedKnowledgeBase);
              
              // Update form data
              const updatedFormData = {
                ...localFormData,
                knowledge_base: clearedKnowledgeBase
              };
              setLocalFormData(updatedFormData);
              
              // Notify parent component of the change
              if (onFormDataChange) {
                onFormDataChange(updatedFormData);
              }
              
              // Clear session manager KB config
              if (sessionManager) {
                sessionManager.clearKnowledgeBaseConfig();
                sessionManager.saveState(1, updatedFormData, { completed: false });
              }
              
              wizardNavigation.saveCurrentStep(updatedFormData, { completed: false });
            }}
            disabled={isLoading || isSubmitting}
            className="mb-6"
          />
          
          {/* Knowledge Base Configuration - Only show when enabled */}
          {console.log('🎯 Conditional rendering check:', { checkboxEnabled, shouldShow: checkboxEnabled }) || null}
          {checkboxEnabled && (
            <KnowledgeBaseConfiguration
              sessionId={formData.session_id}
              parrotId={formData.parrot_id}
              useKnowledgeBase={checkboxEnabled}
              knowledgeBase={knowledgeBase}
              sessionManager={sessionManager}
              onToggle={(enabled) => {
                // Sync with the main toggle handler to ensure consistency
                console.log('🔄 KnowledgeBaseConfiguration toggle clicked:', enabled);
                setCheckboxEnabled(enabled);
                userToggledKnowledgeBaseRef.current = true;
                const updatedFormData = {
                  ...localFormData,
                  use_knowledge_base: enabled
                };
                if (!enabled) {
                  const clearedKnowledgeBase = { url: '', files: [], textContent: [] };
                  setKnowledgeBase(clearedKnowledgeBase);
                  updatedFormData.knowledge_base = clearedKnowledgeBase;
                }
                setLocalFormData(updatedFormData);
                if (onFormDataChange) {
                  onFormDataChange(updatedFormData);
                }
              }}
              onChange={handleKnowledgeBaseChange}
              disabled={isLoading || isSubmitting}
              hideToggle={true} // Hide the internal toggle since we have a dedicated one
              className="mt-4"
            />
          )}
        </div>

        {/* Auto-save and keyboard shortcuts removed */}

        {/* Form Actions */}
        <div className={`${layoutClasses.actions} pt-6`}>
          <button
            type="button"
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                // Use wizard navigation to handle unsaved changes
                wizardNavigation.navigateToRoute('/parrots');
              }
            }}
            className={`${getResponsiveButtonClasses('md')} border rounded-lg font-medium transition-colors ${touchFriendlyClasses.button} ${theme === 'dark'
              ? 'border-gray-600 text-gray-300 hover:bg-gray-700 focus:ring-gray-500'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500'
              } focus:outline-none focus:ring-2 focus:ring-offset-2`}
            disabled={isLoading || isSubmitting || isTransitioning}
          >
            Cancel
          </button>

          <button
            type="submit"
            className={`${getResponsiveButtonClasses('md')} rounded-lg font-medium transition-all duration-200 ${touchFriendlyClasses.button} ${canProceed() && !isLoading && !isSubmitting && !isTransitioning
              ? 'bg-teal-600 hover:bg-teal-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 focus:ring-teal-500'
              : 'bg-gray-400 text-gray-200 cursor-not-allowed'
              } focus:outline-none focus:ring-2 focus:ring-offset-2`}
            disabled={!canProceed() || isLoading || isSubmitting || isTransitioning}
          >
            {isLoading || isSubmitting || isTransitioning ? (
              <div className="flex items-center">
                <LoadingSpinner size="sm" />
                <span className="ml-2">
                  {isTransitioning ? 'Transitioning...' : isSubmitting ? 'Validating...' : 'Processing...'}
                </span>
              </div>
            ) : (
              <div className="flex items-center">
                <span>Continue to Voice Selection</span>
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            )}
          </button>
        </div>

        {/* Helper message for failed syncs */}
        {checkboxEnabled && knowledgeBase.files.length > 0 && (() => {
          const failedSyncs = knowledgeBase.files.filter(file => 
            file.sync_status === 'failed' || file.sync_status === 'error'
          );
          
          if (failedSyncs.length > 0) {
            return (
              <div className={`mt-4 p-4 rounded-lg border ${
                theme === 'dark' 
                  ? 'bg-red-900/20 border-red-800 text-red-300' 
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="font-medium">Cannot proceed with failed syncs</p>
                    <p className="text-sm mt-1">
                      {failedSyncs.length} file{failedSyncs.length !== 1 ? 's' : ''} failed to sync. 
                      Please retry the sync or remove the failed files before continuing.
                    </p>
                  </div>
                </div>
              </div>
            );
          }
          return null;
        })()}
      </form>

      {/* Navigation Confirmation Dialog */}
      <WizardNavigationConfirmDialog
        isOpen={wizardNavigation.showConfirmDialog}
        onConfirm={wizardNavigation.handleContinueWithoutSaving}
        onCancel={wizardNavigation.handleCancelNavigation}
        onSaveAndContinue={wizardNavigation.handleSaveAndContinue}
        targetStep={wizardNavigation.navigationIntent?.targetStep}
        targetStepName={wizardNavigation.navigationIntent?.targetStepName}
        changesSummary={wizardNavigation.getChangesSummary()}
        isLoading={wizardNavigation.isLoading}
      />
    </div>
  );
};

export default Step1ParrotConfig;
